import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CallListApiResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  data: any[];
  success: boolean;
  message: string;
  validationErrors: any;
  statusCode: number;
}

@Injectable({
  providedIn: 'root'
})
export class CallListService {

  private readonly baseUrl = `${environment.apiBaseUrl}/CallRequest/patient-call-list`;

  constructor(private http: HttpClient) { }

  /** Maps frontend column names to API field names for sorting */
  private readonly sortColumnMap: { [key: string]: string } = {
    patient_id: 'entityId',
    last_update: 'ModifiedAt',
    status: 'CallResultStatus',
    name: 'Name',
    phone: 'phoneNumber',
    email: 'Email',
    zipcode: 'ZipCode',
    metroArea: 'MetroArea',
    dob: 'DateOfBirth',
    interactions: 'Interactions',
    study: 'Study',
    agentName: 'AgentName',
    agentType: 'AgentType',
    gender: 'Gender',
    poolId: 'PoolId'
  };

  getCallList(
    page: number,
    pageSize: number,
    filters?: { [key: string]: string[] },
    sortBy?: string | null,
    sortOrder?: 'asc' | 'desc' | null,
    search?: string | null
  ): Observable<CallListApiResponse> {
    const headers = new HttpHeaders({
      'API_KEY': environment.apiKey
    });
    
    let params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);

    if (sortBy && sortOrder) {
      const apiSortBy = this.sortColumnMap[sortBy] ?? sortBy;
      params = params.set('sortBy', apiSortBy).set('sortOrder', sortOrder);
    }

    // Build filters: column filters + global search
    const cleanFilters: { [key: string]: string[] } = {};

    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key].length > 0) {
          const filteredValues = filters[key].filter(value => value !== null && value !== undefined && value !== '');
          if (filteredValues.length > 0) {
            let apiKey = key;
            if (key === 'status') {
              apiKey = 'CallResultStatus';
            } else if (key === 'patient_id') {
              apiKey = 'entityId';
            } else if (key === 'phone') {
              apiKey = 'phoneNumber';
            } else if (key === 'last_update') {
              apiKey = 'ModifiedAt';
            } else if (key === 'poolId') {
              apiKey = 'PoolId';
            }
            cleanFilters[apiKey] = filteredValues;
          }
        }
      });
    }

    // Add global search to filters (e.g. filters={"search":["John"]})
    const searchTerm = (search || '').trim();
    if (searchTerm) {
      cleanFilters['search'] = [searchTerm];
    }

    if (Object.keys(cleanFilters).length > 0) {
      params = params.set('filters', JSON.stringify(cleanFilters));
    }

    return this.http.get<CallListApiResponse>(this.baseUrl, { headers, params });
  }

  getCallQueueCount(): Observable<number> {
    const headers = new HttpHeaders({
      'API_KEY': environment.apiKey
    });
    
    const queueUrl = `${environment.apiBaseUrl}/CallRequest/call-queue`;
    return this.http.get<number | { count?: number; queueCount?: number; data?: number }>(queueUrl, { headers })
      .pipe(
        map(response => {
          // Handle if API returns a number directly
          if (typeof response === 'number') {
            return response;
          }
          // Handle if API returns an object with count property
          if (response && typeof response === 'object') {
            return response.count ?? response.queueCount ?? response.data ?? 0;
          }
          return 0;
        })
      );
  }
}
